'use client';

// Collapsible section wrapper. Two important behaviors:
//   1. Children render only when open — pass a function and we'll
//      invoke it on first open. Once mounted, the children stay
//      mounted across subsequent collapse/re-expand cycles so any
//      in-flight state (loaded usage data, paginated activity) isn't
//      thrown away by the user toggling the section.
//   2. The trigger uses a real <button> for keyboard / a11y, with
//      the chevron rotating via CSS.
//
// The whole point: avoid eager-loading every section's API on
// dashboard mount. Each section is opt-in.

import { useState, type ReactNode } from 'react';

export function Collapsible({
  title,
  subtitle,
  defaultOpen = false,
  children,
}: {
  title:        ReactNode;
  /** Optional summary line under the heading, visible while collapsed too. */
  subtitle?:    ReactNode;
  defaultOpen?: boolean;
  /** Lazy children — only invoked once the section opens. */
  children:     () => ReactNode;
}) {
  const [open,    setOpen]    = useState(defaultOpen);
  const [touched, setTouched] = useState(defaultOpen);

  function toggle() {
    setOpen(o => {
      if (!o) setTouched(true);
      return !o;
    });
  }

  return (
    <section>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 text-left rounded-lg ring-1 ring-inset ring-white/5 bg-surface-1 px-5 py-4 hover:bg-surface-2 transition"
      >
        <div className="min-w-0">
          <h2 className="heading-2">{title}</h2>
          {subtitle && <div className="mt-1 text-xs text-ink-faint truncate">{subtitle}</div>}
        </div>
        <span
          aria-hidden="true"
          className={`text-ink-faint text-lg select-none transition-transform ${open ? 'rotate-90' : ''}`}
        >
          ›
        </span>
      </button>
      {/* Mount once, hide via display so React keeps state across collapses.
          Inner section already brings its own card chrome — we don't double-wrap. */}
      {touched && (
        <div className={`mt-3 ${open ? '' : 'hidden'}`}>
          {children()}
        </div>
      )}
    </section>
  );
}
